import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { database, storage, storageRef, uploadBytesResumable, getDownloadURL, ref as dbRef, set, get } from '../firebase';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiUpload, FiVideo, FiImage, FiCheckCircle, FiAlertCircle, FiLoader, FiClock } from 'react-icons/fi';

const Videoads = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { video, userMobile } = location.state || {};

  const adType = 'video';
  const [adFile, setAdFile] = useState(null);
  const [adFilePreview, setAdFilePreview] = useState('');
  const [durationType, setDurationType] = useState('minutes');
  const [durationValue, setDurationValue] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [uploadedAdUrl, setUploadedAdUrl] = useState('');

  if (!video || !userMobile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
        <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg max-w-md w-full text-center border border-blue-100">
          <div className="flex flex-col items-center">
            <FiAlertCircle className="text-4xl text-red-500 mb-4 animate-pulse" />
            <h2 className="text-xl font-bold text-gray-800 mb-4">No video selected for ads</h2>
            <p className="text-gray-600 mb-6">Please go back and select a video to assign ads.</p>
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center justify-center px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg"
            >
              <FiArrowLeft className="mr-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Validate file type and size
      if (!file.type.startsWith('video/')) {
        toast.error('Please select a video file');
        return;
      }
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        toast.error('Video file size should be less than 50MB');
        return;
      }
      setAdFile(file);
      setAdFilePreview(URL.createObjectURL(file));
    } else {
      setAdFile(null);
      setAdFilePreview('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!adFile || !durationValue) {
      toast.error('Please select a file and enter duration');
      return;
    }
    
    setUploading(true);
    setProgress(0);
    setUploadStatus('uploading');
    
    try {
      const sanitizedMobile = (userMobile || '').replace(/[^0-9]/g, '_');
      const ext = adFile.name.split('.').pop() || 'mp4';
      const fileName = `video_ad_${Date.now()}.${ext}`;
      const storagePath = `videoplayer/video/Ads/${sanitizedMobile}/${fileName}`;
      const adStorageRef = storageRef(storage, storagePath);
      
      const uploadTask = uploadBytesResumable(adStorageRef, adFile);
      
      uploadTask.on('state_changed',
        (snapshot) => {
          const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setProgress(percent);
        },
        (error) => {
          setUploadStatus('error');
          setUploading(false);
          toast.error(`Upload failed: ${error.message}`);
        },
        async () => {
          const adUrl = await getDownloadURL(uploadTask.snapshot.ref);
          const videoId = video.id;
          const adsRef = dbRef(database, `videoplayer/videoUpload/${sanitizedMobile}/${videoId}/ads`);
          
          let nextAdId = 1;
          const adsSnap = await get(adsRef);
          if (adsSnap.exists()) {
            const adKeys = Object.keys(adsSnap.val()).map(k => parseInt(k));
            if (adKeys.length > 0) nextAdId = Math.max(...adKeys) + 1;
          }
          
          const adData = {
            adId: nextAdId,
            adType,
            adUrl,
            durationType,
            durationValue,
            uploadedBy: sanitizedMobile,
            uploadedAt: new Date().toISOString(),
            originalFileName: adFile.name,
          };
          
          await set(dbRef(database, `videoplayer/videoUpload/${sanitizedMobile}/${videoId}/ads/${nextAdId}`), adData);
          setUploadStatus('success');
          setUploadedAdUrl(adUrl);
          setUploading(false);
          toast.success('Ad successfully assigned to video!');
        }
      );
    } catch (err) {
      setUploadStatus('error');
      toast.error(`Error: ${err.message}`);
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header with back button */}
        <div className="mb-6 flex items-center">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors font-medium"
          >
            <FiArrowLeft className="mr-2 text-lg" />
            <span className="hidden sm:inline">Back to Videos</span>
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-center flex-1 text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
            Video Ad Assignment
          </h1>
        </div>

        {/* Main content card */}
        <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-blue-100">
          {/* Video preview section */}
          <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FiVideo className="mr-2 text-indigo-600" />
              Selected Video
            </h2>
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="w-full md:w-1/2 lg:w-2/5">
                <div className="relative pt-[56.25%] rounded-lg overflow-hidden shadow-md bg-black">
                  <video 
                    src={video.videoURL} 
                    controls 
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="w-full md:w-1/2 lg:w-3/5">
                <h3 className="text-lg font-bold text-gray-800 mb-2">{video.title || 'Untitled Video'}</h3>
                {video.description && (
                  <p className="text-gray-600 mb-4 text-sm">{video.description}</p>
                )}
                <div className="flex items-center text-sm text-gray-500">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs">
                    {video.duration || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Ad upload form */}
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
              <FiUpload className="mr-2 text-indigo-600" />
              Upload Video Ad
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Ad type (fixed as video) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ad Type</label>
                <div className="relative">
                  <input 
                    type="text" 
                    value="Video Ad" 
                    disabled 
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-4 py-2.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500" 
                  />
                  <FiVideo className="absolute right-3 top-3 text-gray-500" />
                </div>
              </div>

              {/* File upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Video File</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-indigo-400 transition-colors">
                  <div className="space-y-1 text-center">
                    <div className="flex justify-center">
                      <FiUpload className="mx-auto h-10 w-10 text-gray-400" />
                    </div>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none"
                      >
                        <span>Upload a file</span>
                        <input 
                          id="file-upload" 
                          name="file-upload" 
                          type="file" 
                          accept="video/*" 
                          onChange={handleFileChange} 
                          className="sr-only" 
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">MP4, WebM up to 50MB</p>
                  </div>
                </div>
                {adFile && (
                  <p className="mt-2 text-sm text-gray-600 flex items-center">
                    <FiCheckCircle className="mr-1.5 text-green-500" />
                    Selected: {adFile.name} ({(adFile.size / (1024 * 1024)).toFixed(2)} MB)
                  </p>
                )}
              </div>

              {/* Preview of selected file */}
              {adFilePreview && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Video Preview</h3>
                  <div className="relative pt-[56.25%] rounded-lg overflow-hidden bg-black">
                    <video 
                      src={adFilePreview} 
                      controls 
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}

              {/* Duration settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration Type</label>
                  <div className="relative">
                    <select 
                      value={durationType} 
                      onChange={e => setDurationType(e.target.value)} 
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none"
                    >
                      <option value="minutes">Minutes</option>
                      <option value="seconds">Seconds</option>
                    </select>
                    <FiClock className="absolute right-3 top-3 text-gray-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration Value</label>
                  <input 
                    type="number" 
                    min="1" 
                    value={durationValue} 
                    onChange={e => setDurationValue(e.target.value.replace(/[^0-9]/g, ''))} 
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500" 
                    placeholder={`Enter ${durationType}`} 
                    required 
                  />
                </div>
              </div>

              {/* Submit button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={uploading}
                  className={`w-full py-3 px-4 rounded-xl font-semibold text-white shadow-md transition-all duration-200 flex items-center justify-center ${
                    uploading 
                      ? 'bg-indigo-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:shadow-lg'
                  }`}
                >
                  {uploading ? (
                    <>
                      <FiLoader className="animate-spin mr-2" />
                      Uploading ({progress}%)
                    </>
                  ) : (
                    <>
                      <FiUpload className="mr-2" />
                      Assign Ad to Video
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Upload status indicators */}
            {uploadStatus === 'uploading' && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-blue-700">Upload Progress</span>
                  <span className="text-sm font-medium text-blue-700">{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {uploadStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200 flex items-center">
                <FiCheckCircle className="text-2xl text-green-500 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-green-800">Ad successfully assigned!</h3>
                  <p className="text-sm text-green-600 mt-1">The video ad is now linked to your content.</p>
                </div>
              </div>
            )}

            {uploadStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200 flex items-center">
                <FiAlertCircle className="text-2xl text-red-500 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-red-800">Upload failed</h3>
                  <p className="text-sm text-red-600 mt-1">Please try again or check your connection.</p>
                </div>
              </div>
            )}

            {/* Uploaded ad preview */}
            {uploadedAdUrl && (
              <div className="mt-8 border-t border-gray-100 pt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FiVideo className="mr-2 text-indigo-600" />
                  Uploaded Ad Preview
                </h3>
                <div className="relative pt-[56.25%] rounded-lg overflow-hidden bg-black shadow-md">
                  <video 
                    src={uploadedAdUrl} 
                    controls 
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Videoads;